import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Camera, RotateCcw, Check, X } from 'lucide-react';

const CameraCaptureDialog = ({ open, onOpenChange, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [captured, setCaptured] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraFailed, setCameraFailed] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Attach stream to video element whenever the video ref becomes available
  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraActive]);

  // Called directly from a button click (user gesture) — Safari-safe
  const startCamera = useCallback(async (facing) => {
    try {
      stopStream();
      setCameraActive(false);
      setCameraFailed(false);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraFailed(true);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      // Setting cameraActive will render the <video>, then useEffect attaches the stream
      setCameraActive(true);
    } catch {
      setCameraFailed(true);
    }
  }, [stopStream]);

  const handleClose = (v) => {
    if (!v) {
      stopStream();
      setCaptured(null);
      setCameraActive(false);
      setCameraFailed(false);
    }
    onOpenChange(v);
  };

  const switchCamera = () => {
    const next = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(next);
    startCamera(next);
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setCaptured(canvas.toDataURL('image/jpeg', 0.85));
    stopStream();
    setCameraActive(false);
  };

  const retake = () => {
    setCaptured(null);
    startCamera(facingMode);
  };

  const confirm = () => {
    if (captured && onCapture) {
      fetch(captured)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
          onCapture(file);
          handleClose(false);
        });
    }
  };

  const handleNativeCapture = (e) => {
    const file = e.target.files?.[0];
    if (file && onCapture) {
      onCapture(file);
      handleClose(false);
    }
  };

  // Initial state: show two clear options
  const showInitial = !cameraActive && !captured && !cameraFailed;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Take Photo</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {showInitial ? (
            <div className="flex flex-col items-center justify-center bg-muted/30 rounded-lg p-10 gap-5">
              <Camera className="w-14 h-14 text-[#346B4E]" />
              <div className="flex flex-col gap-3 w-full max-w-xs">
                <Button
                  onClick={() => startCamera(facingMode)}
                  className="bg-[#346B4E] hover:bg-[#2a5740] text-white w-full"
                  data-testid="camera-start-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Use Browser Camera
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleNativeCapture}
                  className="hidden"
                  data-testid="camera-native-input"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full"
                  data-testid="camera-native-capture-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Open Device Camera
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                "Open Device Camera" launches your phone or tablet's camera app
              </p>
            </div>
          ) : cameraFailed ? (
            <div className="flex flex-col items-center justify-center bg-muted/30 rounded-lg p-10 gap-4">
              <Camera className="w-12 h-12 text-muted-foreground" />
              <p className="text-sm text-muted-foreground text-center">
                Browser camera not available. Use your device's native camera instead.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleNativeCapture}
                className="hidden"
                data-testid="camera-native-input-fallback"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-[#346B4E] hover:bg-[#2a5740] text-white"
                data-testid="camera-native-fallback-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                Open Device Camera
              </Button>
            </div>
          ) : captured ? (
            <div className="flex items-center justify-center bg-black rounded-lg overflow-hidden">
              <img src={captured} alt="Captured" className="max-w-full max-h-[400px] object-contain" />
            </div>
          ) : (
            <div className="flex items-center justify-center bg-black rounded-lg overflow-hidden min-h-[200px]">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="max-w-full max-h-[400px]"
              />
            </div>
          )}
          <canvas ref={canvasRef} className="hidden" />

          <div className="flex justify-center gap-3">
            {captured ? (
              <>
                <Button variant="outline" onClick={retake} data-testid="camera-retake-btn">
                  <RotateCcw className="w-4 h-4 mr-2" /> Retake
                </Button>
                <Button onClick={confirm} className="bg-[#346B4E] hover:bg-[#2a5740] text-white" data-testid="camera-confirm-btn">
                  <Check className="w-4 h-4 mr-2" /> Use Photo
                </Button>
              </>
            ) : cameraActive ? (
              <>
                <Button variant="outline" size="sm" onClick={switchCamera} data-testid="camera-switch-btn">
                  <RotateCcw className="w-4 h-4" />
                </Button>
                <Button size="lg" onClick={takePhoto} className="rounded-full w-14 h-14 bg-white border-4 border-gray-300 hover:bg-gray-100" data-testid="camera-capture-btn">
                  <div className="w-8 h-8 rounded-full bg-red-500" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleClose(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CameraCaptureDialog;
